// Routes van het beheer. Elk scherm laadt los (code splitting); schermen staan in src/screens/<gebied>/.
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { LockKeyIcon } from "@phosphor-icons/react";
import { EmptyState } from "@bres/ui";
import { AppShell } from "./shell/AppShell.tsx";
import { isPlatformPath } from "./shell/nav.tsx";
import { useI18n } from "./lib/i18n.tsx";
import { useIsPlatformAdmin, useSessionState } from "./lib/session.tsx";

/**
 * Toegang per gebied: het platform alleen voor platformbeheerders (de API en database weigeren het verder ook);
 * klantschermen alleen met een actief lidmaatschap. Een platformbeheerder zonder klant landt op het platform.
 */
function Guard() {
  const { t } = useI18n();
  const { member } = useSessionState();
  const platformAdmin = useIsPlatformAdmin();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (isPlatformPath(path) && !platformAdmin)
    return (
      <EmptyState icon={<LockKeyIcon />} title={t("platform.no_access_title")}>
        {t("platform.no_access_body")}
      </EmptyState>
    );
  if (!isPlatformPath(path) && !member) return <Navigate to="/platform" replace />;
  return <Outlet />;
}

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Guard />
    </AppShell>
  ),
});

const str = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);
const bool = (v: unknown): boolean | undefined => (v === true || v === "true" ? true : undefined);

/** Zoekparameters: allemaal optioneel, zodat links zonder parameters geldig blijven. */
type SearchSpec = Record<string, "s" | "b">;
type SearchOut<S extends SearchSpec> = { [K in keyof S]?: S[K] extends "b" ? boolean : string };
function searchOf<S extends SearchSpec>(spec: S) {
  return (input: SearchOut<S>): SearchOut<S> => {
    const out: Record<string, unknown> = {};
    for (const [key, kind] of Object.entries(spec)) {
      const value = (input as Record<string, unknown>)[key];
      const parsed = kind === "b" ? bool(value) : str(value);
      if (parsed !== undefined) out[key] = parsed;
    }
    return out as SearchOut<S>;
  };
}

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: lazyRouteComponent(() => import("./screens/overview/Overview.tsx")) }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/properties",
    validateSearch: searchOf({ q: "s", status: "s", kind: "s", editorial: "s", attention: "s", sort: "s", view: "s", create: "b" }),
    component: lazyRouteComponent(() => import("./screens/properties/PropertyList.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/properties/$id",
    validateSearch: searchOf({ tab: "s", locale: "s" }),
    component: lazyRouteComponent(() => import("./screens/properties/PropertyEditor.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/leads",
    validateSearch: searchOf({ status: "s", q: "s", lead: "s", mine: "b" }),
    component: lazyRouteComponent(() => import("./screens/crm/Leads.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/contacts",
    validateSearch: searchOf({ q: "s" }),
    component: lazyRouteComponent(() => import("./screens/crm/Contacts.tsx")),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/contacts/$id", component: lazyRouteComponent(() => import("./screens/crm/ContactDetail.tsx")) }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/tasks",
    validateSearch: searchOf({ view: "s" }),
    component: lazyRouteComponent(() => import("./screens/crm/Tasks.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/localization",
    validateSearch: searchOf({ tab: "s", locale: "s", status: "s" }),
    component: lazyRouteComponent(() => import("./screens/localization/Localization.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/website",
    validateSearch: searchOf({ site: "s", channel: "s", show: "s", build: "s" }),
    component: lazyRouteComponent(() => import("./screens/distribution/Website.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/channels",
    validateSearch: searchOf({ channel: "s", view: "s", status: "s", q: "s" }),
    component: lazyRouteComponent(() => import("./screens/distribution/Channels.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/imports",
    validateSearch: searchOf({ status: "s", source: "s" }),
    component: lazyRouteComponent(() => import("./screens/distribution/Imports.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/imports/$runId",
    validateSearch: searchOf({ action: "s", q: "s" }),
    component: lazyRouteComponent(() => import("./screens/distribution/ImportRun.tsx")),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ai", component: lazyRouteComponent(() => import("./screens/system/Ai.tsx")) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/blog", component: lazyRouteComponent(() => import("./screens/content/Blog.tsx")) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/blog/$postId", component: lazyRouteComponent(() => import("./screens/content/routes.tsx"), "BlogPostRoute") }),
  createRoute({ getParentRoute: () => rootRoute, path: "/chat", component: lazyRouteComponent(() => import("./screens/content/Chat.tsx")) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/platform/ai", component: lazyRouteComponent(() => import("./screens/platform/AiOptions.tsx")) }),
  createRoute({ getParentRoute: () => rootRoute, path: "/platform/bres/blog", component: lazyRouteComponent(() => import("./screens/platform/BresSite.tsx"), "BresBlog") }),
  createRoute({ getParentRoute: () => rootRoute, path: "/platform/bres/blog/$postId", component: lazyRouteComponent(() => import("./screens/platform/BresSite.tsx"), "BresBlogPost") }),
  createRoute({ getParentRoute: () => rootRoute, path: "/platform/bres/chat", component: lazyRouteComponent(() => import("./screens/platform/BresSite.tsx"), "BresChat") }),
  createRoute({ getParentRoute: () => rootRoute, path: "/platform", component: lazyRouteComponent(() => import("./screens/platform/Dashboard.tsx")) }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/platform/sites",
    validateSearch: searchOf({ form: "s" }),
    component: lazyRouteComponent(() => import("./screens/platform/Sites.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/platform/sites/$siteId",
    validateSearch: searchOf({ form: "s" }),
    component: lazyRouteComponent(() => import("./screens/platform/SiteDetail.tsx")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings",
    validateSearch: searchOf({ tab: "s", member: "s", q: "s", area: "s", type: "s", actor: "s", object: "s" }),
    component: lazyRouteComponent(() => import("./screens/system/Settings.tsx")),
  }),
];

export const router = createRouter({
  routeTree: rootRoute.addChildren(routes),
  defaultPreload: "intent",
  defaultViewTransition: true,
  scrollRestoration: true,
  // Het werkvel is op desktop de scrollcontainer; bij navigatie naar boven, bij terug herstellen.
  scrollToTopSelectors: ["#sheet"],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
